import type { FunctionComponent } from 'react'
import styled from '@emotion/styled'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { OrderMethod } from '@/libs/helpers/utils'
import type { OrderingAddress } from '@/providers/googleMaps'
import { Grid, SvgIcon, TextField } from '@/tbui'

const StyledGridWrapper = styled(Grid)`
  box-shadow: ${(props) => props.theme.shape.SHADOW};
  border-radius: ${(props) => props.theme.shape.RADIUS};
  padding: 12px;
  @media (max-width: ${(props) => props.theme.breakpoints.MD}) {
    display: none;
  }
  height: 60px;
`

const StyledAddressField = styled(TextField)`
  width: 200px;
  cursor: pointer;
  text-overflow: ellipsis;
`

const StyledScheduledField = styled(TextField)`
  width: 280px;
  cursor: pointer;
`

interface OrderStatusBarProps {
  openOrderMethodSelector: () => void
  orderingAddress?: OrderingAddress
  scheduledFor?: string
  orderMethod: OrderMethod
}

const OrderStatusBar: FunctionComponent<OrderStatusBarProps> = ({
  openOrderMethodSelector,
  orderingAddress,
  scheduledFor,
  orderMethod,
}) => {
  // HOOKS
  const { t } = useTranslation()

  // CONST
  const isDelivery = orderMethod === OrderMethod.delivery

  const displayAddress =
    orderingAddress?.address1 && orderingAddress?.city
      ? `${orderingAddress?.address1}, ${orderingAddress?.city}`
      : (orderingAddress?.formattedAddress ?? '')

  return (
    <>
      {isDelivery && orderingAddress != null ? (
        <StyledGridWrapper
          template="auto 2fr auto"
          data-test="delivery-address-bar"
          align="center"
          gap="0.5rem"
          justify="center"
          onClick={openOrderMethodSelector}
          role="button"
        >
          <SvgIcon color="PRIMARY" fontSize="20px" name="location" />
          <StyledAddressField
            label={t(
              `${
                displayAddress !== ''
                  ? 'order_method_selector.address_input'
                  : 'delivery_address_bar_placeholder'
              }`
            )}
            name="delivery-address"
            data-test="delivery-address-bar-value"
            value={displayAddress}
            variant="INSET"
            readOnly
            data-dd-privacy="mask"
          />
        </StyledGridWrapper>
      ) : null}
      {scheduledFor != null && scheduledFor !== '' ? (
        <StyledGridWrapper
          template="auto 2fr auto"
          data-test="scheduled-time-bar"
          align="center"
          gap="0.5rem"
          justify="center"
          onClick={openOrderMethodSelector}
          role="button"
        >
          <SvgIcon color="PRIMARY" fontSize="20px" name="time" />
          <StyledScheduledField
            label={t('order_method_selector.scheduled_input')}
            name="scheduled-time-bar-value"
            data-test="scheduled-time-bar-value"
            value={`${t('date', {
              value: DateTime.fromISO(scheduledFor).toISODate(),
            })} - ${t('time', {
              value: DateTime.fromISO(scheduledFor),
            })}`}
            variant="INSET"
            readOnly
          />
        </StyledGridWrapper>
      ) : null}
    </>
  )
}

export default OrderStatusBar
